import { useCallback, useEffect, useMemo, useState } from "react";
import { ChevronRight, RotateCcw, Search, X } from "lucide-react";
import { toast } from "sonner";
import { promptsApi, type PromptTemplate } from "../lib/prompts-api";
import { useConfirm } from "@/components/confirm-dialog";
import { EmptyState } from "@/components/empty-state";
import { ErrorState, PageSkeleton } from "@/components/layout";
import { PageHeader } from "@/components/page-header";
import { SaveBarProvider, useSaveBarForm } from "@/components/save-bar";
import { StatusBadge } from "@/components/status-badge";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Field, FieldDescription, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from "@/components/ui/input-group";
import { Textarea } from "@/components/ui/textarea";
import { useUrlParam } from "@/hooks/use-url-state";

// Per-key display metadata. `group` buckets prompts into sections; `label` is
// the human-friendly title. Keys not listed here fall back to the raw key and
// land in the "Weitere" group.
const PROMPT_META: Record<string, { label: string; group: string }> = {
  preview_cover_swap: {
    label: "Kunden-Preview: Kopf-Tausch im Cover",
    group: "Preview",
  },
  preview_cover_swap_photo_outfit: {
    label: "Kunden-Preview: Kopf + Outfit vom Foto im Cover",
    group: "Preview",
  },
  character_clothed: {
    label: "Character-Generierung (Preview-Fallback)",
    group: "Character",
  },
  character_clothed_photo_outfit: {
    label: "Character-Generierung (Preview-Fallback, Outfit vom Foto)",
    group: "Character",
  },
  character_angle_sheet: {
    label: "Character-Angle-Sheet (global)",
    group: "Character",
  },
  character_angle_sheet_photo_outfit: {
    label: "Character-Angle-Sheet (Outfit vom Foto)",
    group: "Character",
  },
  photo_outfit_extraction: {
    label: "Outfit-Extraktion vom Kinderfoto",
    group: "Analyse & QA",
  },
  photo_outfit_suitability: {
    label: "Kleidungs-Check beim Character-Sheet (Outfit vom Foto)",
    group: "Analyse & QA",
  },
  preview_photo_gate: {
    label: "Foto-Check vor der Vorschau (Outfit vom Foto)",
    group: "Preview",
  },
  scene_placement: { label: "Szenen-Platzierung (global)", group: "Szenen" },
  scene_outfit_detail: {
    label: "Outfit-Szenen (nur Foto-Outfit)",
    group: "Szenen",
  },
  outfit_extraction: { label: "Outfit-Extraktion", group: "Analyse & QA" },
  char_identity_extraction: {
    label: "Identitäts-Extraktion",
    group: "Analyse & QA",
  },
  scene_pose_extraction: {
    label: "Szenen-Ableitung (Kopf, Licht, Sheet-Ansicht)",
    group: "Analyse & QA",
  },
  scene_quality_check: {
    label: "Szenen-Qualitätscheck",
    group: "Analyse & QA",
  },
};

const GROUP_ORDER = [
  "Preview",
  "Character",
  "Szenen",
  "Analyse & QA",
  "Weitere",
];

function meta(key: string) {
  return PROMPT_META[key] ?? { label: key, group: "Weitere" };
}

/** Distinct `{{variable}}` names referenced in a prompt body. */
function extractVars(content: string): string[] {
  const found = new Set<string>();
  for (const m of content.matchAll(/\{\{\s*([a-zA-Z_][a-zA-Z0-9_]*)\s*\}\}/g)) {
    found.add(m[1]);
  }
  return [...found];
}

export default function Prompts() {
  const [prompts, setPrompts] = useState<PromptTemplate[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useUrlParam("q", "");

  const reload = useCallback(() => {
    promptsApi
      .list()
      .then((r) => {
        setPrompts(r.prompts);
        setError(null);
      })
      .catch((err) => setError((err as Error).message));
  }, []);

  useEffect(reload, [reload]);

  const groups = useMemo(() => {
    if (!prompts) return [];
    const q = query.trim().toLowerCase();
    const byGroup = new Map<string, PromptTemplate[]>();
    for (const p of prompts) {
      const m = meta(p.key);
      if (
        q &&
        ![m.label, p.key, p.description].some((v) =>
          v.toLowerCase().includes(q),
        )
      )
        continue;
      if (!byGroup.has(m.group)) byGroup.set(m.group, []);
      byGroup.get(m.group)!.push(p);
    }
    for (const list of byGroup.values()) {
      list.sort((a, b) => meta(a.key).label.localeCompare(meta(b.key).label));
    }
    return GROUP_ORDER.filter((g) => byGroup.has(g)).map((g) => ({
      name: g,
      items: byGroup.get(g)!,
    }));
  }, [prompts, query]);

  if (error && !prompts)
    return (
      <div>
        <PageHeader title="Prompts" />
        <ErrorState message={error} onRetry={reload} />
      </div>
    );
  if (!prompts) return <PageSkeleton rows={4} />;

  return (
    <SaveBarProvider>
      <PageHeader
        title="Prompts"
        meta={
          <>
            Platzhalter wie <code>{"{{name}}"}</code> werden beim Rendern
            ersetzt, unbekannte bleiben stehen.
          </>
        }
      />

      <InputGroup className="mb-4 h-8 max-w-sm bg-card">
        <InputGroupAddon>
          <Search />
        </InputGroupAddon>
        <InputGroupInput
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={`${prompts.length} Prompts durchsuchen`}
          aria-label="Prompts durchsuchen"
        />
        {query && (
          <InputGroupAddon align="inline-end">
            <InputGroupButton
              size="icon-xs"
              onClick={() => setQuery("")}
              aria-label="Suche leeren"
            >
              <X />
            </InputGroupButton>
          </InputGroupAddon>
        )}
      </InputGroup>

      {groups.length === 0 ? (
        <Card>
          <EmptyState
            icon={Search}
            title="Keine Treffer"
            description={`Kein Prompt passt zu „${query.trim()}“.`}
            action={
              <Button
                size="sm"
                variant="secondary"
                onClick={() => setQuery("")}
              >
                Suche zurücksetzen
              </Button>
            }
          />
        </Card>
      ) : (
        <div className="space-y-6">
          {groups.map((group) => (
            <section key={group.name} className="space-y-2">
              <h2 className="flex items-center gap-2 text-sm font-semibold">
                {group.name}
                <span className="text-xs font-normal text-muted-foreground">
                  {group.items.length}
                </span>
              </h2>
              {group.items.map((p) => (
                <PromptCard key={p.key} prompt={p} onChange={reload} />
              ))}
            </section>
          ))}
        </div>
      )}
    </SaveBarProvider>
  );
}

function PromptCard({
  prompt,
  onChange,
}: {
  prompt: PromptTemplate;
  onChange: () => void;
}) {
  const confirm = useConfirm();
  const [open, setOpen] = useState(false);
  const [content, setContent] = useState(prompt.content);
  const [description, setDescription] = useState(prompt.description);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    setContent(prompt.content);
    setDescription(prompt.description);
  }, [prompt]);

  const dirty =
    content !== prompt.content || description !== prompt.description;
  const vars = useMemo(() => extractVars(content), [content]);
  const { label } = meta(prompt.key);

  async function save(): Promise<boolean> {
    setErr(null);
    try {
      await promptsApi.update(prompt.key, { content, description });
      toast.success(`„${label}“ gespeichert`);
      onChange();
      return true;
    } catch (e) {
      setErr((e as Error).message);
      toast.error(`${label}: ${(e as Error).message}`);
      return false;
    }
  }

  useSaveBarForm({
    label,
    dirty,
    save,
    discard: () => {
      setContent(prompt.content);
      setDescription(prompt.description);
      setErr(null);
    },
  });

  async function reset() {
    const ok = await confirm({
      title: `„${label}“ auf Standard zurücksetzen?`,
      description:
        "Der gespeicherte Prompt-Text und die Beschreibung werden durch den Standard ersetzt. " +
        "Das gilt sofort für alle neuen Jobs.",
      confirmLabel: "Auf Standard zurücksetzen",
      destructive: true,
    });
    if (!ok) return;
    setBusy(true);
    setErr(null);
    try {
      await promptsApi.reset(prompt.key);
      toast.success(`„${label}“ zurückgesetzt`);
      onChange();
    } catch (e) {
      setErr((e as Error).message);
      toast.error((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  const lines = Math.min(40, Math.max(12, content.split("\n").length + 2));
  const id = `prompt-${prompt.key}`;

  return (
    <Card className="gap-0 py-0">
      <Collapsible open={open} onOpenChange={setOpen}>
        <CollapsibleTrigger className="group flex w-full flex-wrap items-center gap-x-3 gap-y-1 px-4 py-3 text-left outline-none hover:bg-muted/60 focus-visible:ring-3 focus-visible:ring-ring/50">
          <ChevronRight
            className="size-4 shrink-0 text-muted-foreground transition-transform group-data-[state=open]:rotate-90"
            aria-hidden
          />
          <span className="font-semibold">{label}</span>
          <code className="font-mono text-xs text-muted-foreground">
            {prompt.key}
          </code>
          {dirty && <StatusBadge tone="warning">Nicht gespeichert</StatusBadge>}
          <span className="ml-auto text-xs text-muted-foreground">
            geändert {new Date(prompt.updatedAt).toLocaleDateString("de-DE")}
          </span>
          {!open && (
            <span className="line-clamp-2 w-full pl-7 text-muted-foreground">
              {description || "Keine Beschreibung"}
            </span>
          )}
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="space-y-4 border-t px-4 py-4">
            <Field>
              <FieldLabel htmlFor={`${id}-desc`}>Beschreibung</FieldLabel>
              <Input
                id={`${id}-desc`}
                className="bg-card"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Kurz: wann wird dieser Prompt verwendet?"
              />
            </Field>
            <Field>
              <FieldLabel htmlFor={`${id}-content`}>Prompt-Text</FieldLabel>
              <Textarea
                id={`${id}-content`}
                className="bg-card font-mono text-xs leading-relaxed"
                value={content}
                rows={lines}
                spellCheck={false}
                onChange={(e) => setContent(e.target.value)}
              />
              {vars.length > 0 && (
                <FieldDescription className="flex flex-wrap items-center gap-1">
                  Platzhalter:
                  {vars.map((v) => (
                    <Badge
                      key={v}
                      variant="neutral"
                      className="font-mono"
                    >{`{{${v}}}`}</Badge>
                  ))}
                </FieldDescription>
              )}
            </Field>
            {err && <p className="text-destructive">{err}</p>}
            {prompt.hasDefault && (
              <div className="flex justify-end">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={reset}
                  disabled={busy}
                >
                  <RotateCcw />
                  Auf Standard zurücksetzen
                </Button>
              </div>
            )}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
}
